import { formatAxiosConfig } from "@becon-team/becon-fe-lib/my-axios";
import { validateZodSchema } from "@becon-team/becon-fe-lib/my-zod";
import { objectToQueryString } from "utils/@common/utils_common";

import { axiosInstance } from "libs/axiosInstance";

import {
  DeleteSkinCandidate,
  GetSkinAnalysisResult,
  GetSkinAnalysisResultResponseSchema,
  GetMacroSkinAnalysisStatus,
  GetMacroSkinAnalysisStatusResponseSchema,
  GetMicroSkinAnalysisStatus,
  GetMicroSkinAnalysisStatusResponseSchema,
  GetSkinAnalysisSet,
  GetSkinAnalysisSetResponseSchema,
  GetSkinInquiry,
  GetSkinInquiryResponseSchema,
  PostSkinAnalysisAnalyze,
  PostSkinAnalysisSet,
  PutSkinAnalysisSetDuplicate,
  PutSkinInquiry,
  PostSkinAnalysisSetResponseSchema,
  GetSkinHistoryStatisticsResponseSchema,
  GetSkinHistoryStatistics,
  GetMicroSkinAnalysisTrend,
  GetMicroSkinAnalysisTrendResponseSchema,
  GetSkinAverageScoreResponseSchema,
  GetSkinAverageScore,
  GetSkinAnalysisResultB2B,
  GetSkinAnalysisResultB2BResponseSchema,
} from "./skin.schema";
import { convertObjToQueryStr } from "@becon-team/becon-fe-lib/utils";

/** POST: 피부 분석 생성 */
export const postSkinAnalysisSet: PostSkinAnalysisSet = async ({ userId }) => {
  const serverRes = await axiosInstance.post(`/my/user/${userId}/skin-analysis/create`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    PostSkinAnalysisSetResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 피부 분석 상태 조회 (query x) */
export const getSkinAnalysisStatus: GetMacroSkinAnalysisStatus = async ({ userId, skinAnalysisSetId, type }) => {
  const serverRes = await axiosInstance.get(
    `/my/user/${userId}/skin-analysis/${skinAnalysisSetId}/status?type=${type}`,
  );

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetMacroSkinAnalysisStatusResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 마이크로 피부 분석 상태 조회 (query x) */
export const getMicroSkinAnalysisStatus: GetMicroSkinAnalysisStatus = async ({ userId, skinAnalysisSetId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis/set/${skinAnalysisSetId}/micro/status`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetMicroSkinAnalysisStatusResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** POST: 피부 분석 진행 */
export const postSkinAnalysisAnalyze: PostSkinAnalysisAnalyze = async ({ userId, skinAnalysisSetId }) => {
  const serverRes = await axiosInstance.post(`/my/user/${userId}/skin-analysis/${skinAnalysisSetId}/analyze`);

  return serverRes;
};

/** GET: 피부 분석 결과 조회 */
export const getSkinAnalysisResult: GetSkinAnalysisResult = async ({ userId, skinAnalysisSetId, type }) => {
  const query = objectToQueryString({ type });

  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis/${skinAnalysisSetId}/result?${query}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetSkinAnalysisResultResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** PUT: 피부 분석 복사 */
export const putSkinAnalysisSetDuplicate: PutSkinAnalysisSetDuplicate = async ({ userId, skinAnalysisSetId }) => {
  const serverRes = await axiosInstance.put(`/my/user/${userId}/skin-analysis/${skinAnalysisSetId}`);

  return serverRes.data;
};

/** DELETE: 피부 매크로 후보 삭제 */
export const deleteSkinMacroCandidate: DeleteSkinCandidate = async ({ userId, skinAnalysisSetId, candidateId }) => {
  const serverRes = await axiosInstance.delete(`/my/user/${userId}/skin-analysis/set/${skinAnalysisSetId}/macro`, {
    data: {
      subsetId: candidateId,
    },
  });

  return serverRes;
};

/** DELETE: 피부 마이크로 후보 삭제 */
export const deleteSkinMicroCandidate: DeleteSkinCandidate = async ({ userId, skinAnalysisSetId, candidateId }) => {
  const serverRes = await axiosInstance.delete(`/my/user/${userId}/skin-analysis/set/${skinAnalysisSetId}/micro`, {
    data: {
      subsetId: candidateId,
    },
  });

  return serverRes;
};

/**GET: 피부 문진 조회 */
export const getSkinInquiry: GetSkinInquiry = async ({ userId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis/inquiry`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetSkinInquiryResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** PUT: 피부 문진 수정 */
export const putSkinInquiry: PutSkinInquiry = async ({ userId, ...body }) => {
  const serverRes = await axiosInstance.put(`/my/user/${userId}/skin-analysis/inquiry`, {
    analysisType: "BEFORE", // B2B 피부 Before, after와의 충돌으로 인한 payload
    cleansingStatus: "BEFORE", // B2B 피부 Before, after와의 충돌으로 인한 payload
    ...body,
  });

  return serverRes.data;
};

/** GET: 피부 날짜별 통계 히스토리 조회 */
export const getSkinHistoryStatistics: GetSkinHistoryStatistics = async ({
  accountType,
  subsetType,
  status,
  orderBy,
  userId,
  unit,
  timeOffset,
  take,
  skip,
}) => {
  const queryStr = convertObjToQueryStr({ accountType, subsetType, status, orderBy, unit, timeOffset, take, skip });

  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis/b2c/history/stat?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetSkinHistoryStatisticsResponseSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

/** GET: 피부 히스토리 조회 */
export const getSkinAnalysisSet: GetSkinAnalysisSet = async ({
  accountType,
  subsetType,
  status,
  userId,
  fromDate,
  toDate,
  skip,
  take,
  subsetStatus,
  timeOffset,
}) => {
  const queryStr = convertObjToQueryStr({
    accountType,
    subsetType,
    status,
    fromDate,
    toDate,
    skip,
    take,
    subsetStatus,
    timeOffset,
  });

  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis/b2c/history?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetSkinAnalysisSetResponseSchema,
    formatAxiosConfig(serverRes.config),
  );
  return validatedData;
};

/** GET: 피부 마이크로 분석 트렌드 조회 */
export const getMicroSkinAnalysisTrend: GetMicroSkinAnalysisTrend = async ({ userId, timeOffset }) => {
  const queryStr = convertObjToQueryStr({
    timeOffset,
  });
  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis/b2c/micro/trend?${queryStr}`);

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetMicroSkinAnalysisTrendResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 피부 평균 점수 조회 */
export const getSkinAverageScore: GetSkinAverageScore = async ({ userId, type }) => {
  const serverRes = await axiosInstance.get(
    `/my/user/${userId}/skin-analysis/composite-score/average?subsetType=${type}`,
  );

  const validatedData = await validateZodSchema(
    serverRes.data,
    GetSkinAverageScoreResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};

/** GET: 피부 분석 결과 조회 B2B*/
export const getSkinAnalysisResultB2B: GetSkinAnalysisResultB2B = async ({ userId, skinAnalysisSetId }) => {
  const serverRes = await axiosInstance.get(`/my/user/${userId}/skin-analysis-v2/${skinAnalysisSetId}/result`);

  const validatedData = validateZodSchema(
    serverRes.data,
    GetSkinAnalysisResultB2BResponseSchema,
    formatAxiosConfig(serverRes.config),
  );

  return validatedData;
};
